"use client";

import { useState } from "react";
import { ChevronDown, Search as SearchIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import type { FilterGroup, FiltersV2State } from "./types";

interface FilterPanelProps {
  groups: FilterGroup[];
  state: FiltersV2State;
  onToggleMulti: (param: string, value: string) => void;
  onSetSingle: (param: string, value: string | null) => void;
  onSetRange: (param: string, value: [number, number] | null) => void;
}

/**
 * The panel rendered inside both <FiltersSidebar> (desktop) and <FiltersDrawer>
 * (mobile). Each group is an accordion; long option lists are searchable.
 */
export function FilterPanel({
  groups,
  state,
  onToggleMulti,
  onSetSingle,
  onSetRange,
}: FilterPanelProps) {
  return (
    <div className="divide-y">
      {groups.map((g) => (
        <FilterGroupAccordion
          key={g.id}
          group={g}
          values={state.values[g.param] ?? null}
          onToggleMulti={onToggleMulti}
          onSetSingle={onSetSingle}
          onSetRange={onSetRange}
        />
      ))}
    </div>
  );
}

function FilterGroupAccordion({
  group,
  values,
  onToggleMulti,
  onSetSingle,
  onSetRange,
}: {
  group: FilterGroup;
  values: string | string[] | [number, number] | null;
  onToggleMulti: (param: string, value: string) => void;
  onSetSingle: (param: string, value: string | null) => void;
  onSetRange: (param: string, value: [number, number] | null) => void;
}) {
  const [open, setOpen] = useState(group.defaultOpen ?? true);
  const [query, setQuery] = useState("");

  const activeCount = (() => {
    if (Array.isArray(values) && typeof values[0] === "string") return values.length;
    if (typeof values === "string") return 1;
    if (Array.isArray(values) && typeof values[0] === "number") return 1;
    return 0;
  })();

  const filteredOptions =
    group.options?.filter((o) =>
      query ? o.label.toLowerCase().includes(query.toLowerCase()) : true
    ) ?? [];

  return (
    <div className="py-3">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-left"
      >
        <span className="text-sm font-bold flex items-center gap-2">
          {group.label}
          {activeCount > 0 && (
            <span className="text-[10px] font-bold rounded-full bg-primary text-primary-foreground px-1.5 py-0.5">
              {activeCount}
            </span>
          )}
        </span>
        <ChevronDown
          className={cn("w-4 h-4 transition-transform", open && "rotate-180")}
        />
      </button>

      {open && (
        <div className="mt-3 space-y-1.5">
          {group.searchable && (group.options?.length ?? 0) > 8 && (
            <div className="relative mb-2">
              <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input
                type="search"
                placeholder={`Buscar en ${group.label.toLowerCase()}…`}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full h-8 pl-7 pr-2 rounded-lg border border-input text-xs bg-background"
              />
            </div>
          )}

          {group.kind === "range" ? (
            <RangeInput group={group} value={values as [number, number] | null} onChange={(v) => onSetRange(group.param, v)} />
          ) : (
            <ul
              className={cn(
                "space-y-0.5",
                (group.options?.length ?? 0) > 10 &&
                  "max-h-64 overflow-y-auto pr-1 -mr-1"
              )}
            >
              {filteredOptions.map((opt) => {
                const selected =
                  group.kind === "multi"
                    ? Array.isArray(values) && (values as string[]).includes(opt.value)
                    : values === opt.value;

                return (
                  <li key={opt.value}>
                    <label
                      className={cn(
                        "flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg text-xs cursor-pointer transition-colors",
                        selected
                          ? "bg-primary/10 text-primary font-semibold"
                          : "hover:bg-muted text-foreground"
                      )}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <input
                          type={group.kind === "multi" ? "checkbox" : "radio"}
                          name={group.param}
                          checked={selected}
                          onChange={() => {
                            if (group.kind === "multi") {
                              onToggleMulti(group.param, opt.value);
                            } else {
                              onSetSingle(
                                group.param,
                                selected ? null : opt.value
                              );
                            }
                          }}
                          className="accent-primary shrink-0"
                        />
                        <span className="truncate">{opt.label}</span>
                      </span>
                      <span className="flex items-center gap-1.5 shrink-0">
                        {opt.meta && (
                          <span className="text-[9px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                            {opt.meta}
                          </span>
                        )}
                        {opt.count !== undefined && (
                          <span className="text-[10px] text-muted-foreground tabular-nums">
                            {opt.count}
                          </span>
                        )}
                      </span>
                    </label>
                  </li>
                );
              })}
              {filteredOptions.length === 0 && (
                <li className="text-[11px] text-muted-foreground px-2 py-2">
                  Sin coincidencias.
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function RangeInput({
  group,
  value,
  onChange,
}: {
  group: FilterGroup;
  value: [number, number] | null;
  onChange: (v: [number, number] | null) => void;
}) {
  const min = group.min ?? 0;
  const max = group.max ?? 10000;
  const [localMin, localMax] = value ?? [min, max];

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={min}
          max={max}
          value={localMin}
          placeholder={String(min)}
          onChange={(e) => {
            const v = Number(e.target.value);
            onChange([Number.isFinite(v) ? v : min, localMax]);
          }}
          className="flex-1 h-8 px-2 rounded-lg border border-input text-xs bg-background"
        />
        <span className="text-muted-foreground text-xs">—</span>
        <input
          type="number"
          min={min}
          max={max}
          value={localMax}
          placeholder={String(max)}
          onChange={(e) => {
            const v = Number(e.target.value);
            onChange([localMin, Number.isFinite(v) ? v : max]);
          }}
          className="flex-1 h-8 px-2 rounded-lg border border-input text-xs bg-background"
        />
      </div>
      <DualRangeSlider
        min={min}
        max={max}
        step={group.step ?? 1}
        value={[localMin, localMax]}
        onChange={(v) => onChange(v)}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
        >
          Restablecer rango
        </button>
      )}
    </div>
  );
}

function DualRangeSlider({
  min,
  max,
  step,
  value,
  onChange,
}: {
  min: number;
  max: number;
  step: number;
  value: [number, number];
  onChange: (v: [number, number]) => void;
}) {
  const [lo, hi] = value;
  const pctLo = ((lo - min) / (max - min)) * 100;
  const pctHi = ((hi - min) / (max - min)) * 100;

  return (
    <div className="relative h-6 mt-1">
      <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1 rounded-full bg-muted" />
      <div
        className="absolute top-1/2 -translate-y-1/2 h-1 rounded-full bg-primary"
        style={{ left: `${pctLo}%`, right: `${100 - pctHi}%` }}
      />
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={lo}
        onChange={(e) => onChange([Math.min(Number(e.target.value), hi - step), hi])}
        className="absolute inset-0 w-full appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-background [&::-webkit-slider-thumb]:shadow-sm"
      />
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={hi}
        onChange={(e) => onChange([lo, Math.max(Number(e.target.value), lo + step)])}
        className="absolute inset-0 w-full appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-background [&::-webkit-slider-thumb]:shadow-sm"
      />
    </div>
  );
}
